import * as React from "react";
import { IRcaState } from "../../store/IRcaState";
import { RouteComponentProps } from "react-router-dom";
import { RcaThunkDispatch } from "../../actions/actionTypes";
import { connect } from "react-redux";
import * as testInputActions from "../../actions/testInputActions";
import * as oAuthActions from "../../features/oauth/oauthSlice";
import { useEffect } from "react";
import { ITestInputForm } from "../../models/ITestInputForm";
import { mergeStyles, MessageBar, MessageBarType } from "@fluentui/react";
import { IServerValidationError } from "../../models/IServerValidationError";
import { IOAuthAccount } from "../../models/IOAuthAccount";
import { TestInputForm } from "./TestInputForm";
import { Utils } from "../../Utils";

/**
 * route parameters for this component
 */
interface IRouteParams {
  /**
   * The test id
   */
  testId: string;
}

/**
 * The test input page dispatch props
 */
interface ITestInputPageDispatch {
  /**
   * Loads the test input form
   */
  loadTestInput: (language: string, testId: string) => void;

  /**
   * Updates the form data
   */
  updateFormData: (parameters: Record<string, unknown>) => void;

  /**
   * Executes the test
   */
  executeTest: (language: string, testId: string, parameters: Record<string, unknown>) => void;

  /**
   * Clears any server side validation errors
   */
  clearServerSideValidationErrors: () => void;

  /**
   * Sets the focused field ID
   */
  setFocusedFieldId: (id: string) => void;

  /**
   * Acquires a token interactively
   */
  acquireTokenInteractive: () => void;

  /**
   * Refreshes the OAuth accounts
   */
  refreshOAuthAccounts: () => void;

  /**
   * Set the username hint
   */
  setUserNameHint: (hint: string) => void;
}

/**
 * The test input page state props
 */
interface ITestInputPageState {
  /**
   * The testId for which the input form should be rendered
   */
  testId: string;

  /**
   * The current language
   */
  language: string;

  /**
   * The language direction
   */
  langDir: string;

  /**
   * Whether auto-fill is enabled for the form
   */
  autoFillEnabled: boolean;

  /**
   * The test input form to render
   */
  testInputForm: ITestInputForm | null;

  /**
   * The form data
   */
  formData: Record<string, unknown>;

  /**
   * The server validation errors
   */
  serverValidationErrors: IServerValidationError[];

  /**
   * The error trying to execute the test
   */
  executeError: string;

  /**
   * The focused field ID
   */
  focusedFieldId: string;

  /**
   * The username hint
   */
  userNameHint: string;

  /**
   * The OAuth accounts
   */
  oAuthAccounts: IOAuthAccount[];

  /**
   * Whether a login is in progress
   */
  loginInProgress: boolean;

  /**
   * Whether the OAuth accounts are loading
   */
  accountsLoading: boolean;

  /**
   * A message to display if the loading of OAuth accounts fails
   */
  accountsLoadError: string;

  /**
   * A message to display if the acquiring of a token fails
   */
  acquireTokenError: string;
}

/**
 * The props for the test input page component
 */
export interface ITestInputPageProps extends ITestInputPageDispatch, ITestInputPageState {}

/**
 * The test input page component
 *
 * @param props - the test input page props
 * @returns the markup
 */
export const TestInputPage: React.FC<ITestInputPageProps> = ({
  language,
  testId,
  testInputForm,
  formData,
  serverValidationErrors,
  executeError,
  autoFillEnabled,
  focusedFieldId,
  loadTestInput,
  executeTest,
  updateFormData,
  clearServerSideValidationErrors,
  setFocusedFieldId,
}) => {
  // Load the test input
  useEffect(() => {
    loadTestInput(language, testId);
  }, [language, testId]);

  const styles = mergeStyles({
    padding: 20,
    selectors: {
      [Utils.mobileQuery]: {
        padding: 5,
      },
    },
  });

  return (
    <div className={styles}>
      {!testInputForm ? (
        <div>{WebsiteStrings.Default_cs_HIPLoading}</div>
      ) : (
        <>
          {executeError ? <MessageBar messageBarType={MessageBarType.error}>{executeError}</MessageBar> : <></>}
          <TestInputForm
            formTemplate={testInputForm}
            autoFillEnabled={autoFillEnabled}
            formData={formData}
            focusedFieldId={focusedFieldId}
            setFocusedFieldId={setFocusedFieldId}
            onSubmit={(params): void => executeTest(language, testId, params)}
            onChange={(params): void => updateFormData(params)}
            onErrors={(): void => clearServerSideValidationErrors()}
            serverValidationErrors={serverValidationErrors}
          />
        </>
      )}
    </div>
  );
};

/**
 * Redux connected component MapStateToProps function
 *
 * @param state - The application state
 * @param ownProps - Other properties (from react-router, etc)
 * @returns The state props that will be used by the component
 */
const mapStateToProps = (state: IRcaState, ownProps: RouteComponentProps<IRouteParams>): ITestInputPageState => {
  return {
    testId: ownProps.match.params.testId,
    language: state.globalConfig.language,
    langDir: state.globalConfig.langDir,
    autoFillEnabled: state.globalConfig.autoFillEnabled,
    testInputForm: state.testInput.currentInputForm,
    formData: state.testInput.formData,
    serverValidationErrors: state.testInput.serverValidationErrors,
    executeError: state.testInput.executeError,
    focusedFieldId: state.testInput.focusedFieldId,
    userNameHint: state.testInput.userNameHint,
    oAuthAccounts: state.oauth.accountList,
    accountsLoading: state.oauth.accountsLoading,
    loginInProgress: state.oauth.loginOrLogoutInProgress,
    accountsLoadError: state.oauth.accountsLoadError,
    acquireTokenError: state.oauth.loginOrLogoutError,
  };
};

/**
 * Redux connected component MapDispatchToProps function
 *
 * @param dispatch - The dispatch method
 * @param ownProps - Other properties (from react-router, etc)
 * @returns The set of dispatch delegates
 */
const mapDispatchToProps = (dispatch: RcaThunkDispatch, ownProps: RouteComponentProps<IRouteParams>): ITestInputPageDispatch => {
  return {
    loadTestInput: (language: string, testId: string): void => {
      void dispatch(testInputActions.loadTestInput(language, testId));
    },
    executeTest: (language: string, testId: string, parameters: Record<string, unknown>): void => {
      void dispatch(testInputActions.executeTest(language, testId, parameters, ownProps.history));
    },
    updateFormData: (formData: Record<string, void>): void => {
      dispatch(testInputActions.updateInputFormData(formData));
    },
    clearServerSideValidationErrors: (): void => {
      dispatch(testInputActions.clearServerSideValidationErrors());
    },
    setFocusedFieldId: (id: string): void => {
      dispatch(testInputActions.setFocusedFieldId(id));
    },
    refreshOAuthAccounts: (): void => {
      void dispatch(oAuthActions.refreshOAuthAccounts());
    },
    acquireTokenInteractive: (): void => {
      void dispatch(oAuthActions.acquireTokenInteractive());
    },
    setUserNameHint: (hint: string): void => {
      dispatch(testInputActions.setUserNameHint(hint));
    },
  };
};

// Normal to export redux connected component this way
export default connect(mapStateToProps, mapDispatchToProps)(TestInputPage);
